import React, { useEffect, useState } from 'react';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface GalleryImage {
  src: string;
  alt: string;
  caption?: string;
}
interface GalleryProps {
  images: GalleryImage[];
  className?: string;
  columns?: 1 | 2 | 3 | 4;
  gap?: 'small' | 'medium' | 'large';
  rounded?: boolean;
  lightbox?: boolean;
}
const Gallery: React.FC<GalleryProps> = ({
  images,
  className = '',
  columns = 3,
  gap = 'medium',
  rounded = true,
  lightbox = true
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const columnStyles = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
  };
  const gapStyles = {
    small: 'gap-2',
    medium: 'gap-4',
    large: 'gap-6'
  };
  const roundedStyles = rounded ? 'rounded-lg overflow-hidden' : '';
  const openLightbox = (index: number) => {
    if (lightbox) {
      setActiveIndex(index);
      setLightboxOpen(true);
      document.body.style.overflow = 'hidden';
    }
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };
  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setActiveIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    } else {
      setActiveIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }
  };
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      switch (e.key) {
        case 'ArrowLeft':
          navigateLightbox('prev');
          break;
        case 'ArrowRight':
          navigateLightbox('next');
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);
  return <div className={className} data-id="element-597">
      <div className={`grid ${columnStyles[columns]} ${gapStyles[gap]}`} data-id="element-598">
        {images.map((image, index) => <div key={index} className={`${roundedStyles} overflow-hidden bg-gray-100 aspect-square relative group cursor-pointer`} onClick={() => openLightbox(index)} data-id="element-599">
            <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-600" />
            {image.caption && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-601">
                <p className="text-white text-sm" data-id="element-602">{image.caption}</p>
              </div>}
          </div>)}
      </div>
      {/* Lightbox */}
      {lightboxOpen && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fadeIn" data-id="element-603">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors" aria-label="Close lightbox" data-id="element-604">
            <XIcon className="h-6 w-6" data-id="element-605" />
          </button>
          <button onClick={() => navigateLightbox('prev')} className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors" aria-label="Previous image" data-id="element-606">
            <ChevronLeftIcon className="h-6 w-6" data-id="element-607" />
          </button>
          <button onClick={() => navigateLightbox('next')} className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors" aria-label="Next image" data-id="element-608">
            <ChevronRightIcon className="h-6 w-6" data-id="element-609" />
          </button>
          <div className="max-w-4xl max-h-[80vh] relative" data-id="element-610">
            <img src={images[activeIndex].src} alt={images[activeIndex].alt} className="max-h-[80vh] max-w-full object-contain" data-id="element-611" />
            {images[activeIndex].caption && <div className="absolute inset-x-0 bottom-0 bg-black/50 p-4" data-id="element-612">
                <p className="text-white text-center" data-id="element-613">
                  {images[activeIndex].caption}
                </p>
              </div>}
          </div>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center" data-id="element-614">
            <div className="bg-black/30 rounded-full px-4 py-2 text-white text-sm" data-id="element-615">
              {activeIndex + 1} / {images.length}
            </div>
          </div>
        </div>}
    </div>;
};
export default Gallery;